import {
  AppLayout,
  buildRawPayloadSchema,
  CalendarBlankIcon,
  getCombinationStatus,
  type ContractAnalysisRow,
  LinkSimpleIcon,
  Mtds,
  ScreenHeader,
  toLocaleString,
  TopBar,
  useGetContractsQuery
} from 'prove-common'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { AppLogo } from '../components/app-logo/AppLogo'
import { ReviewCard } from '../components/review-card/ReviewCard'
import { SchemaPropertyList } from '../components/schema-property-list/SchemaPropertyList'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { SubSampleSchemaSection } from '../components/sub-sample-schema-section/SubSampleSchemaSection'
import { TestRequisitionSection } from '../components/test-requisition-section/TestRequisitionSection'

// ---------------------------------------------------------------------------
// Schema section (sample metadata)
// ---------------------------------------------------------------------------

type SchemaSectionProps = {
  schemaJson: string
}

const SchemaSection = ({ schemaJson }: SchemaSectionProps) => {
  const rawPayloadJson = useMemo(() => {
    try {
      const raw = buildRawPayloadSchema(schemaJson)
      return raw ? JSON.stringify(raw, null, 2) : undefined
    } catch {
      return undefined
    }
  }, [schemaJson])

  const downloadSchema = () => {
    const content = rawPayloadJson ?? '{}'
    const blob = new Blob([content], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'schema.json'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Mtds.Card as={Mtds.Grid} data-gap='6'>
      <Mtds.Grid data-gap='0'>
        <Mtds.Heading data-size='xs'>
          Sample metadata{' '}
          <Mtds.Tag data-color={'neutral'} data-icon={'none'}>
            schema
          </Mtds.Tag>
        </Mtds.Heading>
        <Mtds.Muted>
          Validates the <code>metadata</code> field on the requisition — a
          JSON-encoded string with sample-level information (e.g. species, age,
          treatment)
        </Mtds.Muted>
      </Mtds.Grid>
      <SchemaPropertyList schemaJson={schemaJson} />
      {rawPayloadJson != null && (
        <Mtds.Details data-variant='card'>
          <Mtds.Details.Summary>View raw JSON schema</Mtds.Details.Summary>
          <Mtds.Prose>
            <Mtds.Button
              data-variant='secondary'
              data-size='sm'
              onClick={downloadSchema}
            >
              Download JSON schema
            </Mtds.Button>
            <pre className='overflow-x-auto'>{rawPayloadJson}</pre>
          </Mtds.Prose>
        </Mtds.Details>
      )}
    </Mtds.Card>
  )
}

// ---------------------------------------------------------------------------
// Detail screen
// ---------------------------------------------------------------------------

export const CombinationDetailScreen = () => {
  const { analysisTypeId } = useParams()
  const { data: contracts, isPending, isError } = useGetContractsQuery()

  const [copied, setCopied] = useState(false)

  const shareLink = () => {
    navigator.clipboard.writeText(globalThis.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const row: ContractAnalysisRow | undefined = useMemo(() => {
    if (contracts == null) return undefined
    const id = Number(analysisTypeId)
    for (const contract of contracts) {
      if (!contract.etag) continue
      for (const analysisType of contract.analysisTypes ?? []) {
        if (analysisType.id === id) {
          return {
            contractId: contract.id,
            contractEtag: contract.etag,
            analysisType,
            status: getCombinationStatus(analysisType)
          }
        }
      }
    }
    return undefined
  }, [contracts, analysisTypeId])

  const contractTitle = useMemo(
    () => contracts?.find((c) => c.id === row?.contractId)?.title,
    [contracts, row?.contractId]
  )

  const at = row?.analysisType
  const sampleType = at?.analysisType?.sampleType
  const product = sampleType?.product
  const matrix = sampleType?.matrix

  const breadcrumbItems = [
    { label: 'Combinations', to: '/combinations' },
    {
      label:
        product != null && matrix != null
          ? `${product} / ${matrix}`
          : 'Combination'
    }
  ]

  const notFound = !isPending && !isError && row == null

  const substances = useMemo(() => {
    const list = at?.analysisType?.substances ?? []
    return [...list].sort((a, b) =>
      (a.substance?.name ?? '').localeCompare(b.substance?.name ?? '')
    )
  }, [at])

  const schemaName = at?.analysisType?.payloadSchema?.name
  const pageTitle =
    product != null && matrix != null ? `${product} / ${matrix}` : 'Combination'

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      topBar={<TopBar logo={<AppLogo />} breadcrumbItems={breadcrumbItems} />}
      isLoading={isPending}
      isError={isError || notFound}
    >
      {row != null && at != null && (
        <>
          <ScreenHeader
            title={pageTitle}
            actions={
              <Mtds.Button
                key={copied ? 'copied' : 'default'}
                data-variant='tertiary'
                data-size='sm'
                data-tooltip={
                  copied ? 'Link copied!' : 'Copy link to this page'
                }
                onClick={shareLink}
              >
                <LinkSimpleIcon />
                Share
              </Mtds.Button>
            }
            properties={
              at.updatedTime != null
                ? [
                    {
                      label: 'Last updated',
                      icon: <CalendarBlankIcon aria-hidden />,
                      value: toLocaleString(at.updatedTime)
                    }
                  ]
                : []
            }
          />

          {/* Review */}
          <ReviewCard row={row} />

          {/* Details */}
          <Mtds.Grid data-gap='4'>
            <Mtds.Heading data-size='sm'>Details</Mtds.Heading>
            <Mtds.Table data-size='sm'>
              <tbody>
                <tr>
                  <th>Product</th>
                  <td>
                    {product ?? '-'}
                    {sampleType?.productCode != null && (
                      <Mtds.Muted> ({sampleType.productCode})</Mtds.Muted>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Matrix</th>
                  <td>
                    {matrix ?? '-'}
                    {sampleType?.matrixCode != null && (
                      <Mtds.Muted> ({sampleType.matrixCode})</Mtds.Muted>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>Substances</th>
                  <td>
                    {substances.length === 0 ? (
                      <Mtds.Muted>None</Mtds.Muted>
                    ) : (
                      <span className='inline-flex flex-wrap gap-x-1'>
                        {substances.map((entry, i) => (
                          <span key={entry.id}>
                            {entry.substance?.name ?? '-'}
                            {entry.substance?.code != null && (
                              <Mtds.Muted> ({entry.substance.code})</Mtds.Muted>
                            )}
                            {i < substances.length - 1 ? ',' : ''}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>
                    Schema{' '}
                    <Mtds.HelpText>
                      The schema defines the expected metadata fields for this
                      combination. Open the schema sections below to inspect all
                      properties and possible values.
                    </Mtds.HelpText>
                  </th>
                  <td>{schemaName ?? '-'}</td>
                </tr>
              </tbody>
            </Mtds.Table>
          </Mtds.Grid>

          {/* Schema sections */}
          {at.analysisType?.payloadSchema != null && (
            <Mtds.Grid data-gap='6'>
              <SchemaSection
                schemaJson={at.analysisType.payloadSchema.schema}
              />
              <SubSampleSchemaSection
                schemaJson={at.analysisType.payloadSchema.schema}
              />
            </Mtds.Grid>
          )}

          {/* Test requisition */}
          <TestRequisitionSection row={row} contractTitle={contractTitle} />
        </>
      )}
    </AppLayout>
  )
}
